<template>
  <div class="bg-white w-full scroll-styling overflow-y-scroll">
    <div class="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div class="mb-8">
        <button
          @click="goBackToList"
          class="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 bg-transparent border-none cursor-pointer"
        >
          ← Вернуться к списку
        </button>
      </div>

      <div v-if="loading" class="text-center py-16">
        <p class="text-gray-400 text-lg">Загрузка...</p>
      </div>

      <div v-else-if="announcement" class="bg-white rounded-lg shadow-lg overflow-hidden">
        <div class="bg-gradient-to-r from-gray-800 to-gray-700 text-white p-6 md:p-8">
          <h1 class="text-2xl break-words md:text-4xl font-bold mb-6">{{ announcement.title }}</h1>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 text-sm md:text-base">
            <div>
              <span class="opacity-75 text-xs md:text-sm">Компания:</span>
              <p class="text-base md:text-lg font-semibold">{{ announcement.author_name }}</p>
            </div>
            <div>
              <span class="opacity-75 text-xs md:text-sm">Тип объявления:</span>
              <p class="text-base md:text-lg font-semibold">{{ getAnnouncementTypeLabel(announcement.announcement_type) }}</p>
            </div>
            <div v-if="announcement.created_at" class="md:col-span-1">
              <span class="opacity-75 text-xs md:text-sm">Дата размещения:</span>
              <p class="text-base md:text-lg font-semibold">{{ formatDate(announcement.created_at) }}</p>
            </div>
          </div>
        </div>

        <div class="p-4 md:p-8">
          <section class="mb-6 md:mb-8">
            <h2 class="text-xl md:text-2xl font-bold text-gray-800 mb-3 md:mb-4">Описание</h2>
            <p class="text-sm md:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">{{ announcement.description }}</p>
          </section>

          <section v-if="announcement.services && announcement.services.length > 0" class="mb-6 md:mb-8">
            <h2 class="text-xl md:text-2xl font-bold text-gray-800 mb-3 md:mb-4">Услуги</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
              <div
                v-for="service in announcement.services"
                :key="service"
                class="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-300"
              >
                <span class="text-lg mr-3 text-gray-600">✓</span>
                <span class="text-gray-700 font-medium">{{ getServiceLabel(service) }}</span>
              </div>
            </div>
          </section>

          <section class="mb-6 md:mb-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            <div v-if="announcement.budget" class="bg-gray-50 p-4 md:p-6 rounded-lg border border-gray-300">
              <h3 class="text-xs md:text-sm font-semibold text-gray-600 mb-2 md:mb-3 uppercase tracking-wide">Бюджет</h3>
              <p class="text-lg md:text-xl font-bold text-gray-800">{{ announcement.budget }}</p>
            </div>

            <div v-if="announcement.required_pilot_category" class="bg-gray-50 p-4 md:p-6 rounded-lg border border-gray-300">
              <h3 class="text-xs md:text-sm font-semibold text-gray-600 mb-2 md:mb-3 uppercase tracking-wide">Требуемая категория пилота</h3>
              <p class="text-lg md:text-xl font-bold text-gray-800">{{ getPilotCategoryLabel(announcement.required_pilot_category) }}</p>
            </div>

            <div v-if="announcement.location" class="bg-gray-50 p-4 md:p-6 rounded-lg border border-gray-300">
              <h3 class="text-xs md:text-sm font-semibold text-gray-600 mb-2 md:mb-3 uppercase tracking-wide">
                📍 Место проведения
              </h3>
              <p class="text-sm md:text-base text-gray-800">{{ announcement.location }}</p>
            </div>

            <a v-if="contactInfo" :href="`tel:${contactInfo}`" class="block bg-gray-50 p-4 md:p-6 rounded-lg border border-gray-300 hover:bg-gray-100 transition">
              <h3 class="text-xs md:text-sm font-semibold text-gray-600 mb-2 md:mb-3 uppercase tracking-wide">
                📞 Контактная информация
              </h3>
              <p class="text-sm md:text-base text-gray-800 break-words">{{ contactInfo }}</p>
            </a>
          </section>

          <section v-if="announcement.html_content" class="mb-6 md:mb-8">
            <h2 class="text-xl md:text-2xl font-bold text-gray-800 mb-3 md:mb-4">Дополнительная информация</h2>
            <div class="bg-gray-50 p-4 md:p-6 rounded-lg border border-gray-300 prose prose-sm max-w-none text-xs md:text-base" v-html="announcement.html_content"></div>
          </section>

        </div>

        <div class="bg-gray-50 border-t px-4 md:px-8 py-4 md:py-6 space-y-3 md:space-y-0 md:flex md:gap-4">
          <button
            @click="goBackToList"
            class="block px-4 md:px-6 py-2 md:py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium text-sm md:text-base text-center cursor-pointer w-full md:w-auto"
          >
            ← Вернуться к объявлениям
          </button>
          <button
            v-if="announcement && !userAuthenticated"
            @click="showAuthMessage"
            class="block px-4 md:px-6 py-2 md:py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition font-medium text-sm md:text-base text-center w-full md:w-auto"
          >
            📞 Позвонить
          </button>
          <a
            v-else-if="contactInfo"
            :href="`tel:${contactInfo}`"
            class="block px-4 md:px-6 py-2 md:py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition font-medium text-sm md:text-base text-center"
          >
            📞 Позвонить
          </a>
          <button
            v-if="userToken && canRespond"
            @click="showResponseModal = true"
            class="block px-4 md:px-6 py-2 md:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm md:text-base text-center"
          >
            💬 Сообщение
          </button>
        </div>

        <!-- Response Modal -->
        <ResponseCreateModal
          v-if="showResponseModal"
          :announcement-id="announcement.id"
          @close="showResponseModal = false"
          @success="handleResponseSuccess"
        />

      <section class="border-t pt-6 md:pt-8">
          <CommentsListComponent
            v-if="announcement && announcement.id"
            content-type="pilot_announcement"
            :object-id="announcement.id"
            :requires-auth="true"
          />
        </section>
        
      </div>

      <div v-else class="text-center py-16">
        <p class="text-gray-500 text-lg">Объявление не найдено</p>
      </div>


    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import { API_PILOT_ANNOUNCEMENTS_PUBLIC, API_PILOT_SERVICES_PUBLIC, HEADERS_TOKEN_JSON } from '@/const/uri';
import CommentsListComponent from '@/components/Comments/CommentsList.vue';
import ResponseCreateModal from '@/components/ExpertSearch/ResponseCreateModal.vue';

export default {
  name: 'PilotAnnouncementDetail',
  components: {
    CommentsListComponent,
    ResponseCreateModal,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const announcement = ref(null);
    const loading = ref(false);
    const showResponseModal = ref(false);
    const contactInfo = ref(null);
    const serviceLabelMap = ref({});

    const fetchServices = async () => {
      try {
        const response = await axios.get(API_PILOT_SERVICES_PUBLIC.list, {
          headers: HEADERS_TOKEN_JSON()
        });
        const services = response.data.data || [];
        const labelMap = {};
        services.forEach(service => {
          labelMap[service.key] = service.name;
        });
        serviceLabelMap.value = labelMap;
      } catch (error) {
        console.error('Error fetching pilot services:', error);
      }
    };

    const fetchAnnouncement = async () => {
      loading.value = true;
      try {
        const response = await axios.get(
          `${API_PILOT_ANNOUNCEMENTS_PUBLIC.base}${route.params.id}/`,
          { headers: HEADERS_TOKEN_JSON() }
        );

        if (response.data && response.data.data) {
          announcement.value = response.data.data.data;
        }

        const isAuthenticated = store.state.user && store.state.user.role && store.state.user.role.key !== -1;
        if (isAuthenticated) {
          await fetchContactInfo();
        }
      } catch (error) {
        console.error('Error fetching announcement:', error);
      } finally {
        loading.value = false;
      }
    };

    const fetchContactInfo = async () => {
      try {
        const response = await axios.get(
          `${API_PILOT_ANNOUNCEMENTS_PUBLIC.base}${route.params.id}/contact-info/`,
          { headers: HEADERS_TOKEN_JSON() }
        );

        if (response.data && response.data.data) {
          contactInfo.value = response.data.data.data.contact_info;
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
        contactInfo.value = null;
      }
    };

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };

    const getAnnouncementTypeLabel = (type) => {
      const labels = {
        request: 'Запрос на услугу (от работодателя)',
        offer: 'Предложение услуги (от пилота)',
      };
      return labels[type] || type;
    };

    const getServiceLabel = (service) => {
      // First check if we have the service label from API
      if (serviceLabelMap.value && serviceLabelMap.value[service]) {
        return serviceLabelMap.value[service];
      }
      // If not found, return the service key as is
      return service;
    };

    const getPilotCategoryLabel = (category) => {
      const labels = {
        first: 'Первая категория',
        second: 'Вторая категория',
        third: 'Третья категория',
        fourth: 'Четвёртая категория',
      };
      return labels[category] || category;
    };

    const getStatusLabel = (status) => {
      const labels = {
        pending: 'На рассмотрении',
        approved: 'Одобрено',
        rejected: 'Отклонено',
        archived: 'Архивировано',
      };
      return labels[status] || status;
    };

    const getStatusClass = (status) => {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-800',
        approved: 'bg-green-100 text-green-800',
        rejected: 'bg-red-100 text-red-800',
        archived: 'bg-gray-100 text-gray-800',
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    };

    const userToken = computed(() => {
      return localStorage.getItem('user_token') !== null;
    });

    const userAuthenticated = computed(() => {
      return store.state.user && store.state.user.role && store.state.user.role.key !== -1;
    });

    const canRespond = computed(() => {
      const key = store.state.user?.role?.key;
      return key === 2 || key === 4;
    });

    const showAuthMessage = () => {
      alert('Зарегистрируйтесь/авторизируйтесь, чтобы связаться с автором объявления');
    };

    const handleResponseSuccess = () => {
      showResponseModal.value = false;
      // Optionally refresh the data or show a success message
    };

    const goBackToList = () => {
      if (announcement.value?.announcement_type) {
        const type = announcement.value.announcement_type === 'request' ? 'requests' : 'offers';
        router.push({
          name: 'ExpertSearch',
          query: { type }
        });
      } else {
        router.push({ name: 'ExpertSearch' });
      }
    };

    onMounted(() => {
      fetchServices();
      fetchAnnouncement();
    });

    return {
      announcement,
      loading,
      showResponseModal,
      userToken,
      userAuthenticated,
      canRespond,
      contactInfo,
      formatDate,
      getAnnouncementTypeLabel,
      getServiceLabel,
      getPilotCategoryLabel,
      getStatusLabel,
      getStatusClass,
      showAuthMessage,
      handleResponseSuccess,
      goBackToList,
    };
  },
};
</script>

<style scoped>
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose strong {
  color: #111827;
  font-weight: 600;
}

.prose a {
  color: #2563eb;
  text-decoration: none;
}

.prose a:hover {
  text-decoration: underline;
}

.prose p {
  margin-bottom: 1rem;
}

.prose h3 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}
</style>
